// Lib
import React, { CSSProperties, RefObject } from 'react';
import classNames from 'classnames';

// Utils
import {
    getBackgroundColor,
    getCaption,
    getElementId,
    getImageThumbnail,
    getImageWidth,
    getIsTransparentImage,
    getShowCaption,
} from '../../../../../../common/elements/utils/elementPropertyUtils';
import { toCssUrl } from '../../../../../utils/cssUtil';
import { getColorCssValue } from '../../../../utils/elementColorStyleUtils';

// Components
import Caption from '../../../../../components/caption/Caption';
import ElementImage from '../../../../../components/images/ElementImage';
import ImageDrawing from '../../../ImageDrawing';

// Hooks
import { useImageModalV2ZoomInteractions } from './hooks/useImageModalV2ZoomInteractions';
import { useImageModalHasZoomedIn } from './hooks/useImageModalZoomedImageQuality';

// Constants
import { IMAGE_TYPES } from '../../../../../../common/media/mediaConstants';

import { ImMNElement } from '../../../../../../common/elements/elementModelTypes';
import { UseImageModalZoomResult } from '../../hooks/useImageModalZoom';

type ImageModalV2ContentProps = {
    element: ImMNElement;
    lowerQualityImageUrl?: string;
    isEditable: boolean;
    showTransparencyGrid: boolean;
    isHiddenForEdit?: boolean;
    contentStyle: CSSProperties;
    imageWrapperStyle: CSSProperties;
    captionRef: RefObject<HTMLDivElement>;
    imageAreaRef: RefObject<HTMLDivElement>;
    imageZoom: UseImageModalZoomResult;
    // Fires when the displayed image finishes loading — gates the post-save mode switch.
    onImageLoad?: () => void;
};

type ImageModalV2FrameContentProps = {
    element: ImMNElement;
    lowerQualityImageUrl?: string;
    isEditable: boolean;
    showTransparencyGrid: boolean;
    imageWrapperStyle: CSSProperties;
    captionRef: RefObject<HTMLDivElement>;
    imageQualityWidthPxOverride?: number;
    onImageLoad?: () => void;
};

const ImageModalV2FrameContent: React.FC<ImageModalV2FrameContentProps> = React.memo(
    ({
        element,
        lowerQualityImageUrl,
        isEditable,
        showTransparencyGrid,
        imageWrapperStyle,
        captionRef,
        imageQualityWidthPxOverride,
        onImageLoad,
    }) => {
        const elementId = getElementId(element);
        const isTransparent = getIsTransparentImage(element);
        const backgroundColorCss = getColorCssValue(getBackgroundColor(element));
        const imageThumbnailUrl = getImageThumbnail(element);
        const showCaption = getShowCaption(element);
        const imageBackgroundStyle =
            !isTransparent && imageThumbnailUrl ? { backgroundImage: toCssUrl(imageThumbnailUrl) } : undefined;
        const imageWidthPx = Number(imageWrapperStyle.width);
        const imageHeightPx = Number(imageWrapperStyle.height);
        const shouldShowTransparencyGrid =
            showTransparencyGrid && isTransparent && !backgroundColorCss && imageWidthPx > 0 && imageHeightPx > 0;

        return (
            <>
                <div className="ImageModalV2Content-image-wrapper" style={imageWrapperStyle}>
                    <div
                        className={classNames('ImageModalV2Content-image', {
                            'cropper-bg': shouldShowTransparencyGrid,
                        })}
                        style={{
                            ...imageBackgroundStyle,
                            ...imageWrapperStyle,
                        }}
                    >
                        <ElementImage
                            useSecureMediaUrl
                            showBrokenIconOnError
                            element={element}
                            imageType={IMAGE_TYPES.ELEMENT}
                            widthPx={imageWidthPx}
                            imageQualityWidthPxOverride={imageQualityWidthPxOverride}
                            thumbnailUrl={lowerQualityImageUrl}
                            displayFullSize
                            onImageLoad={onImageLoad}
                            key={elementId}
                        />
                        <ImageDrawing element={element} />
                    </div>
                </div>
                {showCaption && (
                    <div ref={captionRef} className="ImageModalV2Content-caption flex-none">
                        <Caption
                            element={element}
                            textContent={getCaption(element)}
                            captionVisible={showCaption}
                            isEditable={isEditable}
                            isModalView
                        />
                    </div>
                )}
            </>
        );
    },
);

ImageModalV2FrameContent.displayName = 'ImageModalV2FrameContent';

/**
 * Renders the image filling the available space while maintaining aspect ratio.
 */
const ImageModalV2Content: React.FC<ImageModalV2ContentProps> = ({
    element,
    lowerQualityImageUrl,
    isEditable,
    showTransparencyGrid,
    isHiddenForEdit,
    contentStyle,
    imageWrapperStyle,
    captionRef,
    imageAreaRef,
    imageZoom,
    onImageLoad,
}) => {
    const backgroundColorCss = getColorCssValue(getBackgroundColor(element));
    const { isPanning, isZoomed, scale } = imageZoom;
    const { zoomViewportRef, panElementRef, frameElementRef, onImageMouseDown } = useImageModalV2ZoomInteractions(
        imageZoom,
        imageAreaRef,
    );

    // Once zoom settles beyond rest scale, request the highest quality image available
    const hasZoomedIn = useImageModalHasZoomedIn({ elementId: getElementId(element), settledScale: scale });
    const imageQualityWidthPxOverride = hasZoomedIn ? getImageWidth(element) : undefined;

    return (
        <div
            ref={zoomViewportRef}
            className={classNames('ImageModalV2Content flex h-full w-full items-center justify-center', {
                'is-zoomed': isZoomed,
                'is-panning': isPanning,
                'is-hidden-for-edit': isHiddenForEdit,
            })}
        >
            <div ref={panElementRef} className="ImageModalV2Content-pan">
                <div
                    ref={frameElementRef}
                    className="ImageModalV2Content-frame"
                    onMouseDown={onImageMouseDown}
                    style={{
                        ...contentStyle,
                        ...(backgroundColorCss && { '--ws-element-background-primary': backgroundColorCss }),
                    }}
                >
                    <ImageModalV2FrameContent
                        element={element}
                        lowerQualityImageUrl={lowerQualityImageUrl}
                        isEditable={isEditable}
                        showTransparencyGrid={showTransparencyGrid}
                        imageWrapperStyle={imageWrapperStyle}
                        captionRef={captionRef}
                        imageQualityWidthPxOverride={imageQualityWidthPxOverride}
                        onImageLoad={onImageLoad}
                    />
                </div>
            </div>
        </div>
    );
};

export default ImageModalV2Content;
